//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logPageView } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { DASHBOARD_MY_CALENDAR, CALENDAR_TYPE_MONTH, PAGE_TITLE_JS_MY_CALENDAR } from '../common/constants';
import { getFormattedDate } from '../common/dateHelper';
import { getUserDateFormat } from '../common/dateHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import AfitdTypography from '../common/elemental/AfitdTypography';
import MonthlyCalendar from './subComponents/MonthlyCalendar';
import WeeklyCalendar from './subComponents/WeeklyCalendar';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';
import { startOfDay } from 'date-fns';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const styles = theme => ({
    root: {
        height: '100%',
        background: '#FAFAFC',
    },
    header: {
        height: '128px',
    },
    content: {
        flex: 1,
        overflowY: 'hidden',
        marginTop: 0,
        marginBottom: 0,
        borderRadius: 10,
        backgroundColor: '#fff',
    },
    calendar: {
        width: '100%',
        height: '100%',
        textAlign: 'center',
    },

    headerTitle: {
        marginTop: '38px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        [theme.breakpoints.down('sm')]: { alignItems: 'flex-start' },
    },
    headerToday: {
        marginTop: '10px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { marginTop: '0px' },
    },
    todayIcon: {
        fontSize: '16px',
        color: 'rgba(17, 30, 20, 0.4)',
    },
    todayContainer: {
        marginTop: '1px',
        marginLeft: '9px',
        fontSize: '13px',
        color: 'rgba(17, 30, 20, 0.6)',
    },
    title: {
        fontStyle: 'normal',
        fontWeight: '500',
        fontSize: '26px',
        lineHeight: '30px',
        color: '#111E14',
        [theme.breakpoints.down('sm')]: { fontSize: 24 },
    },
    confirmedInterviewsContainer: {
        marginTop: '4px',
        marginLeft: '24px',
        display: 'flex',
        [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
    },
    confirmedInterviewsCount: {
        marginRight: '3px',
        color: '#FC821F',
    },
    confirmedInterviewsText: {
        marginRight: '3px',
    },
    confirmedInterviewsCountAndText: { display: 'flex' },
});

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { props, userDateFormat, confirmedInterviewsCount, selectedDate } = params;
    const { setConfirmedInterviewsCount } = params;
    const { classes } = props;

    let defns = {
        root: { item: true, container: true, direction: 'column', lg: 12, md: 12, sm: 12, xs: 12 },
        content: { container: true, item: true, spacing: 0 },
        confirmedInterviewsCount: {
            variant: 'body2',
            value: `${confirmedInterviewsCount}`,
        },
        confirmedInterviewsText: {
            variant: 'body2',
            value: `Confirmed interview${confirmedInterviewsCount !== 1 ? 's' : ''} on`,
        },
        confirmedInterviewDateText: {
            variant: 'body2',
            value: getFormattedDate(selectedDate),
        },
        todayText: {
            variant: 'caption',
            value: getFormattedDate(new Date(), userDateFormat),
            clsBigCaption: true,
        },
        monthlyCalendar: {
            setConfirmedInterviewsCount,
        },
        weeklyCalendar: {
            setConfirmedInterviewsCount,
        },
    };
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const MyCalendar = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    const userDateFormat = getUserDateFormat(appContext);
    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [calendarType, setCalendarType] = useState(CALENDAR_TYPE_MONTH);
    const [confirmedInterviewsCount, setConfirmedInterviewsCount] = useState(0);
    const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_JS_MY_CALENDAR);
    }, []);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ props, userDateFormat, confirmedInterviewsCount, setConfirmedInterviewsCount, selectedDate });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, calendarType, setCalendarType, selectedDate, setSelectedDate }}>
            <AfitdPageHead title={PAGE_TITLE_JS_MY_CALENDAR} />
            <Grid {...defns.root}>
                <Grid {...defns.header}>
                    <div {...defns.headerTitle}>
                        <div {...defns.title}>{DASHBOARD_MY_CALENDAR}</div>

                        {smMatches ? (
                            <div {...defns.confirmedInterviewsContainer}>
                                <div {...defns.confirmedInterviewsCountAndText}>
                                    <AfitdTypography {...defns.confirmedInterviewsCount} />
                                    <AfitdTypography {...defns.confirmedInterviewsText} />
                                </div>
                                <AfitdTypography {...defns.confirmedInterviewDateText} />
                            </div>
                        ) : (
                            <div {...defns.confirmedInterviewsContainer}>
                                <AfitdTypography {...defns.confirmedInterviewsCount} />
                                <AfitdTypography {...defns.confirmedInterviewsText} />
                                <AfitdTypography {...defns.confirmedInterviewDateText} />
                            </div>
                        )}
                    </div>
                    <div {...defns.headerToday}>
                        <CalendarTodayIcon {...defns.todayIcon} />
                        <div {...defns.todayContainer}>
                            <AfitdTypography {...defns.todayText} />
                        </div>
                    </div>
                </Grid>
                <Grid {...defns.content}>
                    <div {...defns.calendar}>
                        {calendarType === CALENDAR_TYPE_MONTH && <MonthlyCalendar {...defns.monthlyCalendar} />}
                        {calendarType !== CALENDAR_TYPE_MONTH && <WeeklyCalendar {...defns.weeklyCalendar} />}
                    </div>
                </Grid>
            </Grid>
        </LayoutContext.Provider>
    );
};

export default withStyles(styles)(MyCalendar);
